import React, { useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { notificationCardStyles } from "./style";
import GetImages from "@/components/GetImages";
import Button from "@/components/Button";

import {
  INotificationDaum,
  INotificationEvents,
} from "@/models/ApiRequestResponse/notification";
import { IButtonVariant } from "@/models/button";
import { IProfileDropdownEnums } from "@/models/doctor";

import {
  capitalizeFirstLetter,
  topBarDropdownDispatchingEnums,
  transformDateToMMDDYYY,
} from "@/utils";
import { useNotificationNavigation } from "@/utils/hooks/useNotificationNavigation";

import { useAppDispatch } from "@/store/hooks";
import { setTopBarDropdownItems } from "@/store/slices/doctor/topNavigation";
import { setIsShowNotification } from "@/store/slices/config";

const GenericNotificationCard: React.FC<INotificationDaum> = (props) => {
  const { title, description, event_type, created_at, dynamic_data } = props;
  const coverImage = dynamic_data?.cover_image;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { handleNotificationClick } = useNotificationNavigation();

  const getIconName = useMemo(() => {
    switch (event_type) {
      case INotificationEvents.PATIENT_HEALTH_HUB:
        return "TelevisionNotificationIcon";
      case INotificationEvents.PATIENT_ASSESSMENT:
        return "AssessmentNotificationIcon";
      case INotificationEvents.TEST_ORDER_COMPLETED:
      case INotificationEvents.PATIENT_TESTS:
      case INotificationEvents.TEST_ORDER_REJECTED:
      case INotificationEvents.TEST_REPORT_UPLOADED:
      case INotificationEvents.TEST_ORDER_ACCEPTED:
      case INotificationEvents.PENDING_DIAGNOSTIC_ORDER_REQUEST:
        return "TestSmallIcon";
      case INotificationEvents.SIGNUP:
        return "WelcomeHandIcon";
      case INotificationEvents.DOCTOR_SCHEDULE:
      case INotificationEvents.SCHEDULE_AVAILABILITY_NOT_UPDATED:
      case INotificationEvents.CONSULTATION_END_REMINDER:
      case INotificationEvents.APPOINTMENT_BOOKED:
      case INotificationEvents.APPOINTMENT_BOOKED_BY_OTHERS:
      case INotificationEvents.APPOINTMENT_BOOKED_VIDEO_CONSULTATION:
        return "DoctorScheduleIcon";
      case INotificationEvents.PATIENT_CHAT:
      case INotificationEvents.NEW_ADMITTED_PATIENT_CHAT:
      case INotificationEvents.NEW_PATIENT_APPOINTMENT_CHAT:
        return "ChatSelectedIcon";
      case INotificationEvents.PATIENT_MEDICINE:
      case INotificationEvents.PENDING_MEDICINE_ORDER_REQUEST:
      case INotificationEvents.MEDICINE_HOME_DELIVERY_ORDER_COMPLETED:
      case INotificationEvents.MEDICINE_HOME_DELIVERY_ORDER_REJECTED:
      case INotificationEvents.MEDICINE_HOME_DELIVERY_ORDER_ACCEPTED:
      case INotificationEvents.MEDICINE_PICKUP_ORDER_ACCEPTED:
      case INotificationEvents.MEDICINE_PICKUP_ORDER_REJECTED:
      case INotificationEvents.MEDICINE_PICKUP_ORDER_COMPLETED:
        return "MedicineSmallIcon";
      case INotificationEvents.PATIENT_HOME:
        return "";
      case INotificationEvents.APPOINTMENT_CANCELED:
        return "AppointmentCanceledNotificationIcon";
      case INotificationEvents.PATIENT_ADMITTED:
      case INotificationEvents.PATIENT_DISCHARGED:
      case INotificationEvents.HOSPITAL_LINKED_TO_DOCTOR:
      case INotificationEvents.DOCTOR_IP_DASH:
      case INotificationEvents.PATIENT_ADMITTED_BY_OTHERS:
        return "IPDashNotificationIcon";
      case INotificationEvents.CONSULTATION_END_AND_PRESCRIPTION_SHARED:
      case INotificationEvents.NEW_DOCTOR_PRESCRIPTION_TEMPLATE:
        return "PrescriptionNotificationIcon";
      case INotificationEvents.VIDEO_CALL_INVITATION:
      case INotificationEvents.VIDEO_CONSULTATION_REMINDER:
        return "VideoConsultationNotificationIcon";
      case INotificationEvents.IN_CLINIC_APPOINTMENT_REMINDER:
      case INotificationEvents.CLINIC_LINKED_TO_DOCTOR:
      case INotificationEvents.CLINIC_PROFILE:
      case INotificationEvents.APPOINTMENT_BOOKED_IN_CLINIC:
        return "ClinicConsultationNotificationIcon";
      default:
        return "";
    }
  }, [event_type]);

  const ctaHandler = useCallback(() => {
    dispatch(setIsShowNotification(false));
    switch (event_type) {
      case INotificationEvents.PATIENT_HOME:
        navigate("/home");
        return;
      case INotificationEvents.DOCTOR_PROFILE:
        dispatch(
          setTopBarDropdownItems(
            topBarDropdownDispatchingEnums[IProfileDropdownEnums.PROFILE]
          )
        );
        return;
      case INotificationEvents.PATIENT_ASSESSMENT:
        navigate(`/health-assessment?type=Brain`);
        return;
      case INotificationEvents.PATIENT_MEDICINE:
        navigate(`/medicines`);
        return;
      case INotificationEvents.DOCTOR_SCHEDULE:
      case INotificationEvents.SCHEDULE_AVAILABILITY_NOT_UPDATED:
      case INotificationEvents.CONSULTATION_END_REMINDER:
        navigate(`/find-doctor`);
        return;
      case INotificationEvents.PATIENT_CHAT:
      case INotificationEvents.NEW_ADMITTED_PATIENT_CHAT:
      case INotificationEvents.NEW_PATIENT_APPOINTMENT_CHAT:
        navigate(`/chat`);
        return;
      case INotificationEvents.TEST_ORDER_COMPLETED:
      case INotificationEvents.PATIENT_TESTS:
        navigate(`/test-reports`);
        return;
      case INotificationEvents.PATIENT_HEALTH_HUB:
        navigate(`/health-hub`);
        return;
      case INotificationEvents.DOCTOR_IP_DASH:
        navigate(`/ip-dash`);
        return;
    }
  }, [event_type]);

  return (
    <Box
      sx={notificationCardStyles.notificationCardWrapper}
      onClick={() => handleNotificationClick(event_type)}
    >
      {coverImage && (
        <img
          src={coverImage}
          alt={title}
          style={{
            width: "100%",
            borderRadius: "0.8rem",
            height: "10.9rem",
            objectFit: "cover",
          }}
        />
      )}

      <Box
        sx={notificationCardStyles.notificationContent}
        mt={coverImage ? 1 : 0}
      >
        <Box sx={notificationCardStyles.headerContainer}>
          {getIconName?.length > 0 && (
            <Box sx={notificationCardStyles.iconWrapper}>
              <GetImages name={getIconName} height={"22"} width={"20"} />
            </Box>
          )}
          <Typography
            sx={notificationCardStyles.notificationTitle}
            variant="h6"
          >
            {title}
          </Typography>
        </Box>
        <Typography
          sx={notificationCardStyles.notificationDescription}
          variant="body2"
        >
          {description}
        </Typography>
        {dynamic_data?.cta_text && (
          <Button variant={IButtonVariant.WHITE} btnTrigger={ctaHandler}>
            {getIconName?.length > 0 && (
              <Box sx={notificationCardStyles.iconWrapper}>
                <GetImages name={getIconName} height={"22"} width={"28"} />
              </Box>
            )}
            {capitalizeFirstLetter(dynamic_data?.cta_text)}
          </Button>
        )}
      </Box>
      <Typography
        sx={notificationCardStyles.notificationTime}
        variant="caption"
      >
        {transformDateToMMDDYYY(created_at, "DD MMM YYYY HH:mm")}
      </Typography>
    </Box>
  );
};

export default GenericNotificationCard;
